import { useEffect, useMemo, useState } from 'react'
import { messageOf } from '../../lib/errors'
import { useHubLang, dateLocaleFor, type HubLang } from '../../lib/i18n'
import { SHOP } from '../../lib/hubCopy'
import { useShopImage } from '../../lib/useShopImage'
import { Dialog } from '../Dialog'
import {
  loadCatalogue, placeOrder,
  blockSentence, offerFor, readsAsPreorder, shortDate,
  type CatalogueItem, type CatalogueRoom, type CatalogueVariant, type ShopImage,
} from '../../lib/shop'

/**
 * The shop floor — the Store half of #/stars. REC-11's buyer side.
 *
 * ── A TILE YOU OPEN, NOT A FORM YOU SCROLL PAST (073, David 15 Sep) ─────────
 *
 * "Before they select color, size, fit ... they can see total stock before they
 * enter the card, and then when they open that up they see total stock by
 * location, and as they make variant selections the stock levels are updated."
 *
 * Every card used to render every axis inline. With the live shirt that is four
 * chip rows, twenty combinations and a room list, times however many items — a
 * grid in which nothing can be compared because each cell is a form. The tile
 * now answers the two questions somebody browsing actually has (what is it, is
 * there any) and the dialog answers the rest.
 *
 * ── THE STOCK NUMBER NARROWS AS THE COMBINATION IS CHOSEN, AND ALL THREE
 *    LEVELS COME FROM THE DATABASE ────────────────────────────────────────────
 *
 * Item total → per room → per combination per room. 065's rule is that `free`
 * is on_hand - held derived server-side at every level it is shown, so 073 put
 * `app.item_free` behind the item figure rather than summing the variants here.
 * A sum in TypeScript would be a second definition of availability, and the
 * first thing it would disagree about is which rooms count: 069 made backorder
 * a POLICY, so a room with no stock that will order one in belongs in the list
 * and a stock-built sum cannot see it. That is the bug 072 fixed one level down.
 *
 * ── THE ROOM FILTER GOES TO THE SERVER, AND ITS OWN OPTIONS DO NOT ─────────
 *
 * `p_room` is passed to `shop_catalogue`, because the counts are SUMMED
 * server-side: narrowing a fetched result here would show every room's total
 * with one room's name on it (seventh time — 039, 044, 046, 054, 062, 065).
 *
 * But the CHIP LIST is built once, from the unfiltered load, and held. Rebuild
 * it from a filtered response and it collapses to the one room you picked, with
 * no way back to the others — a filter that eats its own control. `allRooms` is
 * only ever set when `roomId` is null, and that guard is the whole reason it is
 * a separate piece of state rather than a `useMemo` over `items`.
 */
export type ShopRoomOption = { stockRoomId: string; roomName: string; roomCity: string }

/**
 * Every room anything in the catalogue can be got from, deduped, by name.
 *
 * Pure and exported so it can be tested without a DOM: the two things that can
 * be wrong are listing a room twice when several items stock it, and listing a
 * room nowhere in the catalogue. Built from the ITEM-level rooms, which 073
 * filters to "free stock here, or this room will order it in" — so it is places
 * you could collect from, never a directory of addresses.
 */
export function catalogueRooms(items: CatalogueItem[]): ShopRoomOption[] {
  const seen = new Map<string, ShopRoomOption>()
  for (const item of items) {
    for (const r of item.rooms) {
      if (!seen.has(r.stockRoomId)) {
        seen.set(r.stockRoomId, {
          stockRoomId: r.stockRoomId,
          roomName: r.roomName,
          roomCity: r.roomCity,
        })
      }
    }
  }
  return [...seen.values()].sort((a, b) => a.roomName.localeCompare(b.roomName))
}

/**
 * The stock rows to show: the chosen combination's rooms once one is picked,
 * the item's own otherwise.
 *
 * Pure, and the reason it is a function rather than a ternary inline is that
 * the two shapes must stay interchangeable — the row renders the same way
 * whether it is describing "this item, here" or "this navy medium, here", and
 * the moment they diverge the number changes meaning without changing place.
 */
export function stockRows(
  item: CatalogueItem,
  variant: CatalogueVariant | null,
): CatalogueRoom[] {
  return variant ? variant.rooms : item.rooms
}

export function ShopFloor({
  balanceAvailable,
  onOrdered,
}: {
  balanceAvailable: number
  /**
   * Re-read the balances and the order list: a purchase moves both. Carries
   * the sentence saying what happened, because the dialog that knew it has
   * just closed (Chandra, 25 Sep — see `reload`).
   */
  onOrdered: (notice: string) => void | Promise<void>
}) {
  const t = SHOP[useHubLang()]
  const [items, setItems] = useState<CatalogueItem[] | null>(null)
  const [allRooms, setAllRooms] = useState<ShopRoomOption[]>([])
  const [roomId, setRoomId] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    loadCatalogue(roomId)
      .then((c) => {
        if (!live) return
        setItems(c)
        // Only from the UNFILTERED load — see the header. A filtered response
        // knows about one room, and rebuilding from it strands the reader there.
        if (roomId === null) setAllRooms(catalogueRooms(c))
      })
      .catch((e: Error) => live && setError(messageOf(e)))
    return () => { live = false }
  }, [roomId])

  // ── A SUCCESSFUL ORDER CLOSES THE DIALOG (Chandra and David, 25 Sep) ──────
  //
  // The confirmation used to render at the FOOT of the item dialog, under a
  // list of sixteen stock rooms, so the person who pressed "Order" near the top
  // saw nothing happen and had to scroll to learn it had. Closing puts them back
  // on the store, where "Your orders" is now the FIRST thing (Stars.tsx) and
  // already holds the new row — the order itself is the confirmation, and the
  // sentence travels up to sit beside it. A failed order keeps the dialog open,
  // because the error is about the choice still on screen.
  async function reload(notice: string) {
    setItems(await loadCatalogue(roomId))
    setOpenId(null)
    await onOrdered(notice)
  }

  const open = items?.find((i) => i.itemId === openId) ?? null

  return (
    <section className="shop">
      {error && <p className="composer__error" role="alert">{error}</p>}

      {/* Shown only when there is a choice to make. One room is not a filter,
          it is a fact, and a chip row of one is a control that does nothing. */}
      {allRooms.length > 1 && (
        <div className="shop__rooms">
          <p className="shop__axislabel" id="shop-where">{t.collectFrom}</p>
          <div className="chips" role="group" aria-labelledby="shop-where">
            <button
              type="button"
              className={`chip chip--sm${roomId === null ? ' chip--on' : ''}`}
              aria-pressed={roomId === null}
              onClick={() => setRoomId(null)}
            >
              {t.everywhere}
            </button>
            {allRooms.map((r) => (
              <button
                key={r.stockRoomId}
                type="button"
                className={`chip chip--sm${roomId === r.stockRoomId ? ' chip--on' : ''}`}
                aria-pressed={roomId === r.stockRoomId}
                onClick={() => setRoomId(roomId === r.stockRoomId ? null : r.stockRoomId)}
              >
                {r.roomName}
              </button>
            ))}
          </div>
        </div>
      )}

      {items === null ? (
        <p className="empty" aria-busy="true">{t.loadingShop}</p>
      ) : items.length === 0 ? (
        <p className="empty">
          {roomId === null
            ? t.emptyShop
            : t.nothingThere}
        </p>
      ) : (
        <ul className="shop__grid" aria-label={t.storeLabel}>
          {items.map((item) => (
            <ItemTile key={item.itemId} item={item} onOpen={() => setOpenId(item.itemId)} />
          ))}
        </ul>
      )}

      {/* Keyed on the item so opening a different one resets the chosen
          options rather than carrying a size across from the last thing looked
          at — which would silently resolve to a combination nobody picked. */}
      <Dialog open={open !== null} title={open?.name ?? ''} onClose={() => setOpenId(null)}>
        {open && (
          <ItemDetail
            key={open.itemId}
            item={open}
            balanceAvailable={balanceAvailable}
            onOrdered={reload}
          />
        )}
      </Dialog>
    </section>
  )
}

/**
 * The product shot.
 *
 * ── THE BOX IS RESERVED FROM THE STORED DIMENSIONS, NOT GUESSED ─────────────
 *
 * 019's rule, and the reason `image_width`/`image_height` sit beside the path
 * with a CHECK making them inseparable: `aspect-ratio` from the real numbers
 * means the card is its final height before the picture arrives. Without it a
 * grid of cards reflows as each one loads, under the reader's thumb, on exactly
 * the tethered connections this hub is built for.
 *
 * The alt is EMPTY on the tile and the item name in the dialog. On the tile the
 * picture sits inside a button whose accessible name is already the item, so
 * alt text there makes a screen reader say it twice; in the dialog the heading
 * is elsewhere and the picture is content.
 */
function ShopPicture({
  image,
  alt,
}: {
  image: ShopImage
  alt: string
}) {
  const url = useShopImage(image.path)
  return (
    <div
      className="shop__pic"
      style={{ aspectRatio: `${image.width} / ${image.height}` }}
    >
      {url && <img src={url} alt={alt} loading="lazy" />}
    </div>
  )
}

/**
 * What the tile says about stock, in words.
 *
 * Pure and exported: the interesting cases are zero (which must not read as
 * "0 available" beside a Browse button) and a backorder-only item, which has no
 * stock anywhere and is still orderable. Getting either wrong is a sentence
 * that contradicts the dialog one click later.
 */
export function stockLine(item: CatalogueItem, lang: HubLang = 'en'): string {
  const t = SHOP[lang]
  if (item.free > 0) {
    const where = item.rooms.filter((r) => r.free > 0).length
    return t.inStock(item.free.toLocaleString(), where)
  }
  // 129: a pre-order says so, with its cutoff, rather than "can be ordered in".
  if (readsAsPreorder(item) && item.ordersCloseAt) {
    return t.preorderUntil(shortDate(item.ordersCloseAt, lang))
  }
  if (item.rooms.some((r) => r.canBackorder)) return t.orderInOnly
  return t.outOfStock
}

function ItemTile({ item, onOpen }: { item: CatalogueItem; onOpen: () => void }) {
  const lang = useHubLang()
  return (
    <li className="shop__tile">
      {/* ONE button wrapping the whole tile, and the name is a span rather than
          a heading because a heading inside a button is invalid HTML — the
          button's own text is its accessible name, which is what a screen
          reader needs here anyway. `aria-haspopup` says a dialog is coming, so
          the focus move is expected rather than a jolt. */}
      <button
        type="button"
        className="shop__tilebtn"
        aria-haspopup="dialog"
        onClick={onOpen}
      >
        {item.image && <ShopPicture image={item.image} alt="" />}
        <span className="shop__tilename">{item.name}</span>
        <span className="shop__tilemeta">
          <span className="pill pill--star">★ {item.priceStars.toLocaleString()}</span>
          {/* A LOCKED item says so here rather than showing a count it cannot
              have — but it is still listed, deliberately (065): seeing what a
              badge unlocks is most of the point of the badge. */}
          <span className="shop__tilestock">
            {/* 129: a window that has not opened, or has shut, is a fact about
                the clock and says so — "not available to you" would tell the
                whole company the CYW shirt is for somebody else. */}
            {item.blockReason === 'orders_not_open' || item.blockReason === 'orders_closed'
              ? blockSentence(item, lang)
              : item.blockReason
                ? SHOP[lang].notAvailable
                : stockLine(item, lang)}
          </span>
        </span>
      </button>
    </li>
  )
}

/**
 * Resolve the chosen options to a variant.
 *
 * Matches on the SET of option ids — same length, every one present — because
 * that is what the database stores. Order-insensitive deliberately: the axis
 * order is a rendering decision (REC-29 wants it editable) and a match that
 * depended on it would start failing the day somebody reorders a live item.
 */
export function resolveVariant(
  variants: CatalogueVariant[],
  chosen: string[],
): CatalogueVariant | null {
  if (chosen.length === 0) return variants.length === 1 ? variants[0] : null
  return (
    variants.find(
      (v) =>
        v.optionIds.length === chosen.length &&
        chosen.every((id) => v.optionIds.includes(id)),
    ) ?? null
  )
}

function ItemDetail({
  item,
  balanceAvailable,
  onOrdered,
}: {
  item: CatalogueItem
  balanceAvailable: number
  onOrdered: (notice: string) => Promise<void>
}) {
  const lang = useHubLang()
  const t = SHOP[lang]
  const axes = useMemo(
    () => [...item.axes].sort((a, b) => a.position - b.position),
    [item.axes],
  )
  const [chosen, setChosen] = useState<Record<string, string>>({})
  // 129: which statements the buyer has agreed to. Sent as ids; the server
  // refuses anything other than exactly the item's current set.
  const [agreed, setAgreed] = useState<Record<string, boolean>>({})
  const pledgesOk = item.pledges.every((p) => agreed[p.pledgeId])
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const chosenIds = axes.map((a) => chosen[a.axisId]).filter(Boolean) as string[]
  const complete = chosenIds.length === axes.length
  const variant = complete ? resolveVariant(item.variants, chosenIds) : null
  const rows = stockRows(item, variant)

  async function order(room: CatalogueRoom) {
    if (!variant) return
    setBusy(room.stockRoomId)
    setError(null)
    try {
      await placeOrder(
        variant.variantId,
        room.stockRoomId,
        item.pledges.map((p) => p.pledgeId),
      )
      // Not an optimistic splice: an order takes stars, may take a hold, and
      // may instead join a queue — three outcomes in one transaction, and 028c
      // is this project's worked example of that shape reporting success while
      // writing nothing. Re-read and show what the database holds — which is
      // the parent's job now, because this dialog closes on success.
      await onOrdered(
        room.free > 0
          ? t.reservedAt(room.roomName)
          : readsAsPreorder(item)
            ? t.preorderedAt(room.roomName)
            : t.waitingListAt(room.roomName),
      )
    } catch (e) {
      setError(messageOf(e as Error))
    } finally {
      setBusy(null)
    }
  }

  // What the server resolved for the chosen combination, falling back to the
  // item default before anything is chosen. NOT a ranking rebuilt here — the
  // variant's `image` has already been through app.shop_image_for, so picking
  // Navy swaps to the navy photograph without this file knowing that colour is
  // the photographed axis (070).
  const picture = variant?.image ?? item.image

  if (item.blockReason) {
    return (
      <div className="shopdetail">
        {picture && <ShopPicture image={picture} alt={item.name} />}
        {item.description && <p className="shop__desc">{item.description}</p>}
        <p className="shop__blocked">{blockSentence(item, lang)}</p>
      </div>
    )
  }

  return (
    <div className="shopdetail">
      {picture && <ShopPicture image={picture} alt={item.name} />}

      <p className="shopdetail__price">
        <span className="pill pill--star">★ {item.priceStars.toLocaleString()}</span>
        {/* Affordability is deliberately NOT a block reason (065): an item you
            cannot afford this month is not locked, you are three recognitions
            away from it. Stated as a fact about today. */}
        {balanceAvailable < item.priceStars && (
          <span className="shop__tilestock">
            {t.moreNeeded((item.priceStars - balanceAvailable).toLocaleString())}
          </span>
        )}
      </p>

      {item.description && <p className="shop__desc">{item.description}</p>}

      {item.ordersCloseAt && (
        <p className="shop__closes">
          {t.ordersClose(new Date(item.ordersCloseAt).toLocaleDateString(dateLocaleFor(lang)))}
        </p>
      )}

      {/* 129: PLEDGES. Statements the item asks the buyer to agree to — "I
          will take part in the CYW run". Not a lock (the item is for them);
          a step on the way to ordering, so it sits ABOVE the stores and every
          order button waits for it. Each is a checkbox-role button rather
          than a native input: 046 took the last native checkbox out of this
          app, and the chip shows the state as well as the offer. */}
      {item.pledges.length > 0 && (
        <div className="shop__pledges">
          <p className="shop__axislabel" id={`pl-${item.itemId}`}>{t.pledgeHead}</p>
          <ul className="shop__pledgelist" aria-labelledby={`pl-${item.itemId}`}>
            {item.pledges.map((p) => {
              const on = Boolean(agreed[p.pledgeId])
              return (
                <li key={p.pledgeId} className="shop__pledge">
                  <span className="shop__pledgetext" id={`plt-${p.pledgeId}`}>{p.statement}</span>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    aria-describedby={`plt-${p.pledgeId}`}
                    className={`chip chip--sm${on ? ' chip--on' : ''}`}
                    onClick={() => setAgreed((a) => ({ ...a, [p.pledgeId]: !on }))}
                  >
                    {on ? '✓ ' : ''}{t.pledgeAgree}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {axes.map((axis) => (
        <div key={axis.axisId} className="shop__axis">
          <p className="shop__axislabel" id={`ax-${axis.axisId}`}>{axis.label}</p>
          <div className="chips" role="group" aria-labelledby={`ax-${axis.axisId}`}>
            {axis.options.map((o) => {
              const on = chosen[axis.axisId] === o.optionId
              return (
                <button
                  key={o.optionId}
                  type="button"
                  className={`chip chip--sm${on ? ' chip--on' : ''}`}
                  aria-pressed={on}
                  onClick={() =>
                    setChosen((c) => ({ ...c, [axis.axisId]: on ? '' : o.optionId }))
                  }
                >
                  {o.label}
                </button>
              )
            })}
          </div>
        </div>
      ))}

      {/* WHERE. Present from the moment the dialog opens, showing the item's
          total per room, and NARROWING to the chosen combination as the axes are
          answered — David's ask, and the honest shape besides: stock hangs off
          the variant (065), so "where do I go" only becomes a precise question
          once the size is known. */}
      <div className="shopdetail__stock">
        <p className="shop__axislabel" id={`st-${item.itemId}`}>
          {variant ? t.whereVariant(variant.label) : t.whereItIs}
          {/* 078. The SAME badge recipe the room manager sees on the same
              combination (RoomStock's Retired pill), so the two screens agree
              about what this thing is. It is a fact about the combination, not
              a warning: the row below is a live offer. */}
          {variant?.isRetired && <span className="pill pill--draft">{t.retired}</span>}
        </p>

        {/* WHY THE SENTENCE IS HERE AND NOT ON THE OPTION BUTTON: the pill says
            what it is, and this says what follows from it. `shop_catalogue`
            drops a retired combination the moment the last one goes, so this
            only ever renders beside stock that genuinely exists — and every
            room under it has canBackorder false, set server-side, which is why
            the room rows below already say "none here" rather than offering to
            order one in. */}
        {variant?.isRetired && (
          <p className="shop__stock">
            <strong>{t.lastsHead}</strong> {t.lastsBody}
          </p>
        )}

        {complete && !variant ? (
          <p className="shop__stock">{t.comboNotSold}</p>
        ) : rows.length === 0 ? (
          <p className="shop__stock">
            {t.noRoom}
          </p>
        ) : (
          <ul className="shopdetail__rooms" aria-labelledby={`st-${item.itemId}`}>
            {rows.map((room) => {
              // ONE answer per row for "what will pressing this do" — the same
              // function the single button used, called per room. The label and
              // the disabled state cannot come apart because they are the same
              // object, which is 065's single-block-reason rule one layer up.
              const offer = offerFor(item, variant, room, balanceAvailable, lang)
              return (
                <li className="shopdetail__room" key={room.stockRoomId}>
                  <div className="shopdetail__roommain">
                    <p className="shopdetail__roomname">{room.roomName}</p>
                    <p className="shopdetail__roommeta">
                      {room.roomCity} ·{' '}
                      {/* THREE NUMBERS ARE STORED AND ONLY ONE IS SHOWN. `free`
                          is the only one somebody deciding can act on — on hand
                          and held are the manager's problem, and "4 on the
                          shelf, 4 promised" reads as availability that is not
                          there. */}
                      {room.free > 0
                        ? t.here(room.free.toLocaleString())
                        : room.canBackorder
                          ? t.noneHereOrderIn
                          : t.noneHere}
                    </p>
                  </div>
                  {/* No button at all until a combination is chosen: before
                      that the row is a fact, not an offer. */}
                  {variant && (
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={busy !== null || offer.kind === 'none' || !pledgesOk}
                      onClick={() => order(room)}
                    >
                      {busy === room.stockRoomId
                        ? t.sending
                        : offer.kind !== 'none' && !pledgesOk
                          ? t.pledgeFirst
                          : offer.label}
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        {!variant && axes.length > 0 && (
          <p className="shop__stock">
            {t.chooseToSee(axes.length)}
          </p>
        )}
      </div>

      {error && <p className="composer__error" role="alert">{error}</p>}
    </div>
  )
}
